package se.mittpass.web.components

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.href
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*

@Composable
fun LandingHeader(
    onLogin: () -> Unit,
    onSignUp: () -> Unit = {},
    onNavigate: (String) -> Unit = {},
) {
    var menuOpen by remember { mutableStateOf(false) }
    val windowWidth = rememberWindowWidth()
    val isDesktop = windowWidth >= 1024

    Header({
        classes("landing-header")
        style {
            property("position", "sticky")
            property("top", "0")
            property("z-index", "100")
            property("width", "100%")
            property("box-sizing", "border-box")
            property("background", "color-mix(in srgb, var(--rp-surface) 95%, transparent)")
            property("backdrop-filter", "blur(12px)")
            property("border-bottom", "1px solid var(--rp-hl-med)")
        }
    }) {
        Div({
            classes("landing-header-inner")
            style {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("gap", if (windowWidth >= 640) "1rem" else "0.5rem")
                property("max-width", "1440px")
                property("width", "100%")
                property("box-sizing", "border-box")
                property("margin", "0 auto")
                property("padding", if (windowWidth < 640) "0.65rem 0.85rem" else "0.75rem 1.25rem")
            }
        }) {
            // Logo
            A(attrs = {
                href("/")
                onClick { it.preventDefault(); menuOpen = false; onNavigate("home") }
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.55rem")
                    property("text-decoration", "none")
                    property("flex-shrink", "0")
                    property("cursor", "pointer")
                }
            }) {
                Img(src = "/mittpass_logo.svg", alt = "Mittpass – Schemaläggning, Personalliggare & HACCP") {
                    style {
                        property("width", "34px")
                        property("height", "34px")
                        property("display", "block")
                        property("flex-shrink", "0")
                    }
                }
                if (windowWidth >= 380) {
                    Span({
                        style {
                            property("font-size", "1.1rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("letter-spacing", "-0.3px")
                            property("white-space", "nowrap")
                        }
                    }) { Text("Mittpass") }
                }
            }

            // Desktop Navigation (>= 1024px)
            Nav({
                classes("landing-nav-desktop")
                style {
                    property("display", if (isDesktop) "flex" else "none")
                    property("align-items", "center")
                    property("justify-content", "flex-end")
                    property("gap", if (windowWidth >= 1440) "0.8rem" else "0.6rem")
                    property("min-width", "0")
                    property("flex-shrink", "1")
                }
            }) {
                val dict = LocaleManager.dictionary
                val linkFontSize = if (windowWidth >= 1280) "0.86rem" else "0.82rem"
                navLink(dict.personalliggare, "/personalliggare", linkFontSize) { onNavigate("personalliggare") }
                navLink(dict.schemalagga, "/schemalagga-personal", linkFontSize) { onNavigate("schemalagga") }
                navLink(dict.egenkontroll, "/egenkontroll", linkFontSize) { onNavigate("egenkontroll") }
                if (windowWidth >= 1380) {
                    navLink("Livsmedelskontroll", "/livsmedelskontroll", linkFontSize) { onNavigate("livsmedelskontroll") }
                    navLink("HACCP", "/haccp", linkFontSize) { onNavigate("haccp") }
                }
                navLink(dict.priser, "/#priser", linkFontSize)
                navLink(dict.faq, "/#faq", linkFontSize)

                if (windowWidth >= 1140) {
                    Select({
                        attr("aria-label", "Välj språk")
                        onChange { event ->
                            val selected = se.mittpass.shared.translation.Language.fromCode(event.value)
                            LocaleManager.setLanguage(selected)
                        }
                        style {
                            property("padding", "0.35rem 0.5rem")
                            property("font-size", "0.8rem")
                            property("font-weight", "600")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("border-radius", "6px")
                            property("background", "var(--rp-surface)")
                            property("color", "var(--rp-text)")
                            property("font-family", "inherit")
                            property("cursor", "pointer")
                            property("flex-shrink", "0")
                        }
                    }) {
                        se.mittpass.shared.translation.Language.entries.forEach { lang ->
                            Option(lang.code, {
                                if (lang == LocaleManager.currentLanguage) selected()
                            }) {
                                Text(lang.displayName)
                            }
                        }
                    }
                }

                ThemeSwitcher(isCompact = true)

                Button({
                    onClick { onLogin() }
                    style {
                        property("padding", if (windowWidth >= 1200) "0.5rem 1rem" else "0.45rem 0.75rem")
                        property("font-size", "0.85rem")
                        property("font-weight", "600")
                        property("cursor", "pointer")
                        property("border", "1.5px solid var(--rp-hl-med)")
                        property("border-radius", "8px")
                        property("background", "transparent")
                        property("color", "var(--rp-pine)")
                        property("font-family", "inherit")
                        property("white-space", "nowrap")
                        property("flex-shrink", "0")
                    }
                }) { Text(dict.login) }

                Button({
                    onClick { onSignUp() }
                    style {
                        property("background", "var(--rp-cta-bg)")
                        property("color", "var(--rp-cta-text)")
                        property("padding", if (windowWidth >= 1200) "0.5rem 1rem" else "0.45rem 0.8rem")
                        property("font-size", "0.85rem")
                        property("font-weight", "700")
                        property("border", "none")
                        property("border-radius", "8px")
                        property("font-family", "inherit")
                        property("white-space", "nowrap")
                        property("cursor", "pointer")
                        property("flex-shrink", "0")
                    }
                }) {
                    Text(if (windowWidth >= 1220) "Testa gratis 30 dagar" else "Testa gratis")
                }
            }

            // Mobile & Tablet Controls (< 1024px): ThemeSwitcher + Direct Login button + Hamburger menu toggle
            Div({
                classes("landing-nav-mobile-actions")
                style {
                    property("display", if (!isDesktop) "flex" else "none")
                    property("align-items", "center")
                    property("gap", "0.45rem")
                    property("flex-shrink", "0")
                }
            }) {
                ThemeSwitcher(isCompact = true)
                val dict = LocaleManager.dictionary
                Button({
                    onClick { onLogin() }
                    style {
                        property("padding", "0.42rem 0.75rem")
                        property("font-size", "0.82rem")
                        property("font-weight", "700")
                        property("cursor", "pointer")
                        property("border", "1.5px solid var(--rp-pine)")
                        property("border-radius", "8px")
                        property("background", "transparent")
                        property("color", "var(--rp-pine)")
                        property("font-family", "inherit")
                        property("white-space", "nowrap")
                        property("flex-shrink", "0")
                    }
                }) {
                    Text(dict.login)
                }

                Button({
                    classes("landing-nav-toggle")
                    attr("aria-label", if (menuOpen) "Stäng meny" else "Öppna meny")
                    onClick { menuOpen = !menuOpen }
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("background", "var(--rp-overlay)")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("border-radius", "8px")
                        property("cursor", "pointer")
                        property("padding", "0.42rem 0.6rem")
                        property("font-size", "1.1rem")
                        property("color", "var(--rp-text)")
                        property("flex-shrink", "0")
                    }
                }) {
                    FaIcon(if (menuOpen) "fa-solid fa-xmark" else "fa-solid fa-bars")
                }
            }
        }
    }

    // Mobile full-screen overlay menu
    Div({
        classes("landing-nav-overlay")
        style {
            property("display", if (menuOpen) "flex" else "none")
            property("position", "fixed")
            property("inset", "0")
            property("z-index", "1000")
            property("background", "var(--rp-base)")
            property("flex-direction", "column")
            property("align-items", "center")
            property("justify-content", "center")
            property("gap", "1.25rem")
            property("padding", "1.5rem")
            property("overflow-y", "auto")
        }
    }) {
        // Top right close button
        Button({
            attr("aria-label", "Stäng meny")
            onClick { menuOpen = false }
            style {
                property("position", "absolute")
                property("top", "1.25rem")
                property("right", "1.5rem")
                property("background", "none")
                property("border", "none")
                property("color", "var(--rp-text)")
                property("font-size", "1.75rem")
                property("cursor", "pointer")
                property("padding", "0.5rem")
            }
        }) {
            FaIcon("fa-solid fa-xmark")
        }

        val dict = LocaleManager.dictionary
        A(attrs = { href("/personalliggare"); onClick { it.preventDefault(); menuOpen = false; onNavigate("personalliggare") }; classes("landing-nav-overlay-link") }) { Text(dict.personalliggare) }
        A(attrs = { href("/schemalagga-personal"); onClick { it.preventDefault(); menuOpen = false; onNavigate("schemalagga") }; classes("landing-nav-overlay-link") }) { Text(dict.schemalagga) }
        A(attrs = { href("/egenkontroll"); onClick { it.preventDefault(); menuOpen = false; onNavigate("egenkontroll") }; classes("landing-nav-overlay-link") }) { Text(dict.egenkontroll) }
        A(attrs = { href("/livsmedelskontroll"); onClick { it.preventDefault(); menuOpen = false; onNavigate("livsmedelskontroll") }; classes("landing-nav-overlay-link") }) { Text("Livsmedelskontroll") }
        A(attrs = { href("/haccp"); onClick { it.preventDefault(); menuOpen = false; onNavigate("haccp") }; classes("landing-nav-overlay-link") }) { Text("HACCP") }
        A(attrs = { href("/#priser");    onClick { menuOpen = false }; classes("landing-nav-overlay-link") }) { Text(dict.priser) }
        A(attrs = { href("/#faq");       onClick { menuOpen = false }; classes("landing-nav-overlay-link") }) { Text(dict.faq) }

        Select({
            onChange { event ->
                val selected = se.mittpass.shared.translation.Language.fromCode(event.value)
                LocaleManager.setLanguage(selected)
            }
            style {
                property("padding", "0.55rem 1rem")
                property("font-size", "1rem")
                property("font-weight", "600")
                property("border", "1px solid var(--rp-hl-med)")
                property("border-radius", "8px")
                property("background", "var(--rp-surface)")
                property("color", "var(--rp-text)")
                property("font-family", "inherit")
                property("cursor", "pointer")
            }
        }) {
            se.mittpass.shared.translation.Language.entries.forEach { lang ->
                Option(lang.code, {
                    if (lang == LocaleManager.currentLanguage) selected()
                }) {
                    Text(lang.displayName)
                }
            }
        }

        Button({
            onClick { menuOpen = false; onLogin() }
            classes("landing-nav-overlay-btn")
        }) { Text(dict.login) }

        Button({
            onClick { menuOpen = false; onSignUp() }
            style {
                property("padding", "0.75rem 2rem")
                property("font-size", "1rem")
                property("font-weight", "700")
                property("cursor", "pointer")
                property("background", "var(--rp-cta-bg)")
                property("color", "var(--rp-cta-text)")
                property("border", "none")
                property("border-radius", "10px")
                property("font-family", "inherit")
            }
        }) { Text("Testa gratis 30 dagar") }
    }
}

@Composable
private fun navLink(text: String, anchor: String, fontSize: String = "0.875rem", onClick: (() -> Unit)? = null) {
    A(attrs = {
        href(anchor)
        if (onClick != null) onClick { it.preventDefault(); onClick() }
        style {
            property("color", "var(--rp-subtle)")
            property("text-decoration", "none")
            property("font-weight", "600")
            property("font-size", fontSize)
            property("letter-spacing", "0.01em")
            property("cursor", "pointer")
            property("white-space", "nowrap")
            property("flex-shrink", "0")
        }
    }) { Text(text) }
}
